<template>
  <article class="terrain-question-editor">
    <div class="terrain-question-editor__main">
      <label>Pregunta<input :value="question.label" @input="patchQuestion({ label: $event.target.value })"></label>
      <label>Tipo<TerrainSelect :value="question.type" :options="questionTypeOptions" aria-label="Tipo de pregunta" @input="changeType" /></label>
      <label class="terrain-question-editor__check"><input type="checkbox" :checked="question.required" @change="patchQuestion({ required: $event.target.checked })"> Obligatoria</label>
      <label>Clasificación<TerrainSelect :value="question.privacy" :options="privacyOptions" aria-label="Clasificación de pregunta" @input="patchQuestion({ privacy: $event })" /></label>
    </div>
    <ConditionEditor v-if="!sectionConditional" :value="question.visibility || null" :definition="definition" :target-section-id="sectionId" :target-question-id="question.questionId" @input="patchQuestion({ visibility: $event })" />
    <p v-else class="terrain-question-editor__publication-hint">La visibilidad de esta pregunta depende de su sección.</p>
    <p v-if="question.privacy === 'public'" class="terrain-question-editor__publication-hint">
      Esta pregunta será visible en el mapa y en la tabla de atributos como «{{ derivedFieldId }}».
    </p>
    <div class="terrain-question-editor__actions" aria-label="Acciones de pregunta">
      <button type="button" :disabled="isFirst" @click="$emit('move', { questionId: question.questionId, direction: -1 })">Subir</button>
      <button type="button" :disabled="isLast" @click="$emit('move', { questionId: question.questionId, direction: 1 })">Bajar</button>
      <button type="button" @click="$emit('duplicate', question.questionId)">Duplicar</button>
      <button type="button" class="terrain-question-editor__danger" @click="$emit('remove', question.questionId)">Eliminar</button>
    </div>

    <div v-if="isSelection" class="terrain-question-editor__options">
      <strong>Opciones</strong>
      <div v-for="option in question.options" :key="option.optionId" class="terrain-question-editor__option">
        <input :value="option.label" @input="updateOption(option.optionId, $event.target.value)">
        <button type="button" @click="moveOption(option.optionId, -1)">↑</button>
        <button type="button" @click="moveOption(option.optionId, 1)">↓</button>
        <button type="button" @click="removeOption(option.optionId)">Eliminar</button>
      </div>
      <button type="button" @click="addOption">Agregar opción</button>
    </div>

    <div v-if="question.type === 'table'" class="terrain-question-editor__schema">
      <strong>Esquema de tabla</strong>
      <div class="terrain-question-editor__schema-grid">
        <div>
          <span>Filas</span>
          <div v-for="row in question.table.rows" :key="row.rowId" class="terrain-question-editor__row">
            <input :value="row.label" @input="updateTableRow(row.rowId, { label: $event.target.value })">
            <button type="button" @click="removeTableRow(row.rowId)">Eliminar</button>
          </div>
          <button type="button" @click="addTableRow">Agregar fila</button>
        </div>
        <div>
          <span>Columnas</span>
          <div v-for="column in question.table.columns" :key="column.columnId" class="terrain-question-editor__row">
            <input :value="column.label" @input="updateTableColumn(column.columnId, { label: $event.target.value })">
            <TerrainSelect :value="column.type" :options="cellTypeOptions" aria-label="Tipo de columna" @input="updateTableColumn(column.columnId, { type: $event })" />
            <label class="terrain-question-editor__inline-check"><input type="checkbox" :checked="column.required" @change="updateTableColumn(column.columnId, { required: $event.target.checked })"> Obligatoria</label>
            <button type="button" @click="removeTableColumn(column.columnId)">Eliminar</button>
          </div>
          <button type="button" @click="addTableColumn">Agregar columna</button>
        </div>
      </div>
    </div>

    <div v-if="question.type === 'repeater'" class="terrain-question-editor__schema">
      <strong>Columnas del repetidor</strong>
      <div v-for="column in question.repeater.columns" :key="column.columnId" class="terrain-question-editor__row">
        <input :value="column.label" @input="updateRepeaterColumn(column.columnId, { label: $event.target.value })">
        <TerrainSelect :value="column.type" :options="cellTypeOptions" aria-label="Tipo de columna" @input="updateRepeaterColumn(column.columnId, { type: $event })" />
        <label class="terrain-question-editor__inline-check"><input type="checkbox" :checked="column.required" @change="updateRepeaterColumn(column.columnId, { required: $event.target.checked })"> Obligatoria</label>
        <button type="button" @click="removeRepeaterColumn(column.columnId)">Eliminar</button>
      </div>
      <button type="button" @click="addRepeaterColumn">Agregar columna</button>
    </div>
  </article>
</template>

<script>
import { createOption, createQuestion, createRepeaterColumn, createTableColumn, createTableRow, derivePublication } from "./factories.js";
import TerrainSelect from "@/components/terrain/TerrainSelect.vue";
import ConditionEditor from "./ConditionEditor.vue";

export default {
  name: "QuestionEditor",
  components: { TerrainSelect, ConditionEditor },
  props: {
    question: { type: Object, required: true }, definition: { type: Object, required: true },
    sectionId: { type: String, required: true }, sectionConditional: Boolean,
    isFirst: Boolean, isLast: Boolean,
  },
  data() {
    return {
      questionTypes: ["shortText", "longText", "number", "date", "singleSelect", "multiSelect", "check", "table", "repeater", "location", "file", "photo"],
      cellTypes: ["shortText", "longText", "number", "date", "check", "singleSelect", "multiSelect"],
    };
  },
  computed: {
    isSelection() { return this.question.type === "singleSelect" || this.question.type === "multiSelect"; },
    questionTypeOptions() { return this.questionTypes.map((value) => ({ value, label: this.typeLabel(value) })); },
    cellTypeOptions() { return this.cellTypes.map((value) => ({ value, label: this.typeLabel(value) })); },
    privacyOptions() { return [{ value: "public", label: "Publicable" }, { value: "private", label: "Privado" }]; },
    derivedFieldId() { return derivePublication(this.question).fieldId; },
  },
  methods: {
    typeLabel(type) {
      return {
        shortText: "Texto corto", longText: "Texto largo", number: "Número", date: "Fecha",
        singleSelect: "Selección simple", multiSelect: "Selección múltiple", check: "Casilla",
        table: "Tabla", repeater: "Repetidor", file: "Archivo", photo: "Foto", location: "Ubicación",
      }[type] || type;
    },
    patchQuestion(patch) {
      const next = { ...this.question, ...patch };
      next.publication = next.privacy === "public" ? derivePublication(next) : null;
      this.$emit("update", next);
    },
    changeType(type) {
      const typed = createQuestion(type);
      const next = { ...this.question, type };
      ["options", "table", "repeater"].forEach((key) => {
        if (typed[key]) next[key] = typed[key]; else delete next[key];
      });
      next.publication = next.privacy === "public" ? derivePublication(next) : null;
      this.$emit("update", next);
    },
    updateOptions(options) { this.patchQuestion({ options }); },
    addOption() { this.updateOptions([...(this.question.options || []), createOption()]); },
    updateOption(optionId, label) { this.updateOptions(this.question.options.map((option) => option.optionId === optionId ? { ...option, label } : option)); },
    removeOption(optionId) { this.updateOptions(this.question.options.filter((option) => option.optionId !== optionId)); },
    moveOption(optionId, direction) { this.moveById(this.question.options, "optionId", optionId, direction, this.updateOptions); },
    updateTable(table) { this.patchQuestion({ table }); },
    addTableRow() { this.updateTable({ ...this.question.table, rows: [...this.question.table.rows, createTableRow()] }); },
    updateTableRow(rowId, patch) { this.updateTable({ ...this.question.table, rows: this.question.table.rows.map((row) => row.rowId === rowId ? { ...row, ...patch } : row) }); },
    removeTableRow(rowId) { this.updateTable({ ...this.question.table, rows: this.question.table.rows.filter((row) => row.rowId !== rowId) }); },
    addTableColumn() { this.updateTable({ ...this.question.table, columns: [...this.question.table.columns, createTableColumn()] }); },
    updateTableColumn(columnId, patch) { this.updateTable({ ...this.question.table, columns: this.question.table.columns.map((column) => column.columnId === columnId ? { ...column, ...patch } : column) }); },
    removeTableColumn(columnId) { this.updateTable({ ...this.question.table, columns: this.question.table.columns.filter((column) => column.columnId !== columnId) }); },
    updateRepeater(columns) { this.patchQuestion({ repeater: { ...this.question.repeater, columns } }); },
    addRepeaterColumn() { this.updateRepeater([...(this.question.repeater.columns || []), createRepeaterColumn()]); },
    updateRepeaterColumn(columnId, patch) { this.updateRepeater(this.question.repeater.columns.map((column) => column.columnId === columnId ? { ...column, ...patch } : column)); },
    removeRepeaterColumn(columnId) { this.updateRepeater(this.question.repeater.columns.filter((column) => column.columnId !== columnId)); },
    moveById(items, idKey, id, direction, callback) {
      const next = [...items]; const currentIndex = next.findIndex((item) => item[idKey] === id); const targetIndex = currentIndex + direction;
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= next.length) return;
      const [item] = next.splice(currentIndex, 1); next.splice(targetIndex, 0, item); callback(next);
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-question-editor {
  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-lg);
  padding: var(--terrain-space-md);
  display: grid;
  gap: var(--terrain-space-sm);
}

.terrain-question-editor__main {
  display: grid;
  grid-template-columns: minmax(180px, 2fr) minmax(130px, 1fr) auto minmax(120px, 1fr);
  gap: var(--terrain-space-sm);
  align-items: end;
}

label { display: grid; gap: var(--terrain-space-xs); color: var(--color-navy); font-size: var(--terrain-font-caption); font-weight: 600; }

input {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  font: inherit;
  padding: var(--terrain-space-xs) var(--terrain-space-sm);
  background: var(--color-white);
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover { border-color: var(--color-steel); }
  &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
}

.terrain-question-editor__check { display: flex; padding-bottom: var(--terrain-space-sm); }

.terrain-question-editor__publication-hint { margin: 0; color: var(--color-ghost); font-size: var(--terrain-font-caption); }

.terrain-question-editor__actions,
.terrain-question-editor__option,
.terrain-question-editor__row {
  display: flex;
  gap: var(--terrain-space-xs);
  flex-wrap: wrap;
  align-items: center;
}

button {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  background: var(--color-white);
  color: var(--color-navy);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-xs) var(--terrain-space-sm);
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-mist); border-color: var(--color-steel); }
  &:disabled { cursor: not-allowed; opacity: .45; }
}

.terrain-question-editor__danger { color: var(--color-danger); }
.terrain-question-editor__danger:hover:not(:disabled) { background: var(--terrain-color-danger-light); border-color: var(--color-danger); }

.terrain-question-editor__options, .terrain-question-editor__schema { display: grid; gap: var(--terrain-space-xs); }
.terrain-question-editor__option input, .terrain-question-editor__row input { flex: 1; }
.terrain-question-editor__schema { color: var(--color-ghost); font-size: var(--terrain-font-caption); border-top: 1px solid var(--color-line); padding-top: var(--terrain-space-sm); }
.terrain-question-editor__schema-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--terrain-space-md); }
.terrain-question-editor__schema-grid > div { display: grid; gap: var(--terrain-space-xs); }
.terrain-question-editor__inline-check { display: flex; font-size: var(--terrain-font-caption); }
@media (max-width: 700px) { .terrain-question-editor__main, .terrain-question-editor__schema-grid { grid-template-columns: 1fr; } .terrain-question-editor__check { padding-bottom: 0; } }
</style>
